import React, { useState, useEffect } from 'react';
import { 
    Calendar, 
    ArrowRight, 
    ChevronLeft, 
    ChevronRight 
} from 'lucide-react';
import FormatDate from '../utils/dateTools';
import { API_URL, fetchData } from '../helpers/api';
import TruncatableHtmlContent from '../utils/TruncatableHtmlContent';

const PastEventsPage = () => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [eventsPerPage] = useState(12);

    useEffect(() => {
        fetchPastEvents();
    }, []);

    const fetchPastEvents = async () => {
        try {
            setLoading(true);
            const fetchedEvents = await fetchData(`/api/events?populate=*&filters[isPublished][$eq]=true`);
            
            // Get today's date at the start of the day
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            // Filter for past events
            const pastEvents = fetchedEvents.filter(event => {
                const endDate = event.newEndDate 
                    ? new Date(event.newEndDate) 
                    : new Date(event.newStartDate);
                endDate.setHours(23, 59, 59, 999);

                return endDate < today;
            });

            // Sort past events by end date in descending order (most recent first)
            const sortedPastEvents = pastEvents.sort((a, b) => {
                const endDateA = a.newEndDate ? new Date(a.newEndDate) : new Date(a.newStartDate);
                const endDateB = b.newEndDate ? new Date(b.newEndDate) : new Date(b.newStartDate);
                return endDateB - endDateA;
            });

            setEvents(sortedPastEvents);
        } catch (error) {
            console.error('Failed to fetch past events:', error);
        } finally {
            setLoading(false);
        }
    };

    // Pagination logic
    const indexOfLastEvent = page * eventsPerPage;
    const indexOfFirstEvent = indexOfLastEvent - eventsPerPage;
    const currentEvents = events.slice(indexOfFirstEvent, indexOfLastEvent);
    const totalPages = Math.ceil(events.length / eventsPerPage);

    const renderEventCard = (event) => (
        <div 
            key={event.documentId} 
            className="col"
        >
            <div className="card h-100 border-0 shadow-sm hover-translate-up transition">
                {event?.images && (
                    <div className="card-img-top position-relative">
                        <img 
                            src={API_URL + event.images[0]?.url} 
                            alt={event?.name || ""} 
                            className="w-100 object-fit-cover" 
                            style={{ height: '200px' }} 
                        />
                        {event.isFree && (
                            <span className="position-absolute top-0 end-0 badge bg-success m-2">
                                Gratuit
                            </span>
                        )}
                    </div>
                )}
                <div className="card-body d-flex flex-column">
                    <div className="d-flex align-items-center text-muted small mb-2">
                        <Calendar size={14} className="me-1" />
                        {event.newEndDate ? (
                            <small>
                                <FormatDate dateString={event.newStartDate} format="short" />
                                {" - "}
                                <FormatDate dateString={event.newEndDate} format="short" />
                            </small>
                        ) : (
                            <small>
                                <FormatDate dateString={event.newStartDate} format="long" />
                            </small>
                        )}
                    </div>

                    <h3 className="fs-6 fw-bold mb-2 line-clamp-2">{event.title}</h3>
                    
                    <div className="text-muted small mb-3 flex-grow-1">
                        <TruncatableHtmlContent
                            html={event?.description}
                            truncate={true}
                            maxChars={80}
                        />
                    </div>

                    <div className="mt-auto">
                        <a 
                            href={`/evenement/${event?.documentId}`} 
                            className="text-dark fw-medium d-inline-flex align-items-center text-decoration-none"
                        >
                            Détails
                            <ArrowRight size={14} className="ms-1 transition-transform event-arrow" />
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderPagination = () => {
        const pageNumbers = [];
        
        // Always show first page
        pageNumbers.push(1);
        
        // Add ellipsis and surrounding pages if total pages > 5
        if (totalPages > 5) {
            // Add ellipsis and surrounding pages
            if (page > 3) {
                pageNumbers.push('...');
            }
            
            // Determine range of pages to show
            const startPage = Math.max(2, page - 1);
            const endPage = Math.min(totalPages - 1, page + 1);
            
            for (let i = startPage; i <= endPage; i++) {
                if (!pageNumbers.includes(i)) {
                    pageNumbers.push(i);
                }
            }
            
            // Add ellipsis if not already near the end
            if (page < totalPages - 2) {
                pageNumbers.push('...');
            }
            
            // Always show last page
            if (!pageNumbers.includes(totalPages)) {
                pageNumbers.push(totalPages);
            }
        } else {
            // If total pages is 5 or less, show all pages
            for (let i = 2; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        }

        return pageNumbers;
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <p>Chargement des événements passés...</p>
            </div>
        );
    }

    return (
        <div className="past-events-page container py-4 mt-5">
            <div className="text-center mb-4">
                <h1 className="h2 fw-bold mb-2">ÉVÉNEMENTS PASSÉS</h1>
                <p className="text-muted">
                    Parcourez les événements précédents du MACTe
                </p>
                <a 
                    href="/evenements" 
                    className="btn btn-outline-secondary mt-2"
                >
                    Voir les événements à venir
                </a>
            </div>

            {currentEvents.length === 0 ? (
                <div className="text-center py-5">
                    <p className="fs-4 text-muted">Aucun événement passé trouvé</p>
                </div>
            ) : (
                <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 g-3">
                    {currentEvents.map(renderEventCard)}
                </div>
            )}

            {/* Pagination */}
            <div className="d-flex justify-content-center mt-4">
                <nav aria-label="Page navigation">
                    <ul className="pagination">
                        <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                            <button 
                                className="page-link" 
                                onClick={() => setPage(page - 1)}
                                disabled={page === 1}
                            >
                                <ChevronLeft size={18} />
                            </button>
                        </li>
                        
                        {renderPagination().map((pageNum, index) => (
                            <li 
                                key={index} 
                                className={`page-item ${pageNum === page ? 'active' : ''} ${pageNum === '...' ? 'disabled' : ''}`}
                            >
                                <button 
                                    className="page-link" 
                                    onClick={() => typeof pageNum === 'number' && setPage(pageNum)}
                                >
                                    {pageNum}
                                </button>
                            </li>
                        ))}
                        
                        <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                            <button 
                                className="page-link" 
                                onClick={() => setPage(page + 1)}
                                disabled={page === totalPages}
                            >
                                <ChevronRight size={18} />
                            </button>
                        </li>
                    </ul>
                </nav>
            </div>
        </div>
    );
};

export default PastEventsPage;